import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { cloneDeep, get, isEmpty, pullAt, set } from 'lodash';
import { default as React } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { getFeaturesKeysObjectsWebWorker } from 'utils/features/getFeaturesKeysObjectsWebWorker';
import { useFormatBracketsOptions } from 'utils/formatBrackets/formatBracketsOptions';
import { humanize } from 'utils/helpers';
import { BoxPlot, HistogramPlot, PieChartPlot, ScatterPlot, ViolinPlot } from './FeaturePlots';
import { ConfigurablePlotParams, GenerateStudyReportFormValues } from './types';

export interface PlotProps {
  featuresKeys: string[];
  studyId: string;
  orchestrationId?: string;
  isLoadingFeaturesData: boolean;
  methods: UseFormReturn<GenerateStudyReportFormValues, any>;
  plot: ConfigurablePlotParams;
  featuresPlotIndex: number;
  plotIndex: number;
  checkValidationAndSetIsStepFailed: () => void;
}

export const Plot: React.FC<PlotProps> = ({
  featuresKeys,
  studyId,
  orchestrationId,
  methods,
  plot,
  plotIndex,
  featuresPlotIndex,
  isLoadingFeaturesData,
  checkValidationAndSetIsStepFailed,
}) => {
  const { watch } = methods;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: plot.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
  };
  const formatBracketsOptions = useFormatBracketsOptions({
    addStain: false,
    studyId,
  });

  const formatBracketsOptionsWithOrchestrationId = {
    ...formatBracketsOptions,
    orchestrationId,
  };

  const enabled = !isEmpty(featuresKeys) && !formatBracketsOptions.isLoading && !isLoadingFeaturesData;

  const { data: featuresData, isLoading: isParsingFeaturesDataState } = useQuery({
    queryKey: ['featuresData', featuresKeys, formatBracketsOptionsWithOrchestrationId],
    queryFn: () => {
      return getFeaturesKeysObjectsWebWorker(featuresKeys, formatBracketsOptionsWithOrchestrationId);
    },
    enabled,
    staleTime: Infinity, // getFeaturesKeysObj is a pure function, so we can cache it indefinitely
    retry: false,
  });

  const isLoading = isLoadingFeaturesData || (enabled && isParsingFeaturesDataState);

  const onChange = (e: any, newPlot: ConfigurablePlotParams) => {
    const currentPlots = cloneDeep(watch('configParams.configurablePlots')) || [];
    set(currentPlots, [featuresPlotIndex, 'plotList', plotIndex], newPlot);
    methods.setValue('configParams.configurablePlots', currentPlots);
    checkValidationAndSetIsStepFailed();
  };

  return (
    <Grid container spacing={1} style={style} alignContent={'center'} alignItems="center">
      <Grid
        xs={1}
        item
        sx={{
          cursor: 'grab',
        }}
        {...listeners}
        ref={setNodeRef}
        {...attributes}
        textAlign="center"
      >
        <Grid item>
          <DragHandleIcon />
        </Grid>
        <Grid item>
          <Typography variant="caption" color="textSecondary">
            {plotIndex + 1}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={1}>
        {humanize(plot.plotType)}
      </Grid>

      <Grid item xs={9}>
        {plot.plotType === 'scatter' && (
          <ScatterPlot
            plot={plot}
            featuresData={featuresData || []}
            isLoadingFeaturesData={isLoading}
            onChange={onChange}
          />
        )}
        {plot.plotType === 'histogram' && (
          <HistogramPlot
            plot={plot}
            featuresData={featuresData || []}
            isLoadingFeaturesData={isLoading}
            onChange={onChange}
          />
        )}
        {plot.plotType === 'box' && (
          <BoxPlot
            plot={plot}
            featuresData={featuresData || []}
            isLoadingFeaturesData={isLoading}
            onChange={onChange}
          />
        )}
        {plot.plotType === 'violin' && (
          <ViolinPlot
            plot={plot}
            featuresData={featuresData || []}
            isLoadingFeaturesData={isLoading}
            onChange={onChange}
          />
        )}
        {plot.plotType === 'pie' && (
          <PieChartPlot
            plot={plot}
            featuresData={featuresData || []}
            isLoadingFeaturesData={isLoading}
            onChange={onChange}
          />
        )}
        {plot.plotType === 'quantification_analysis' && (
          <Typography variant="body2">No parameters to configure for this plot.</Typography>
        )}
      </Grid>

      <Grid item xs={1}>
        <DeleteOutlineIcon
          sx={{
            cursor: 'pointer',
          }}
          onClick={() => {
            const currentPlots = cloneDeep(watch('configParams.configurablePlots')) || [];
            const plotsToModify = get(currentPlots, [featuresPlotIndex, 'plotList'], []);
            pullAt(plotsToModify, plotIndex);
            set(currentPlots, [featuresPlotIndex, 'plotList'], plotsToModify);
            methods.setValue('configParams.configurablePlots', currentPlots);
            checkValidationAndSetIsStepFailed();
          }}
        />
      </Grid>
    </Grid>
  );
};
